import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddCustomerModal from '../components/AddCustomerModal';
import { api } from '../utils/api';
import './KYCScreen.css';

export default function KYCScreen() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await api.getCustomers();
        setCustomers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
        setError(err.message || 'Failed to load customers');
        setCustomers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return customers;
    const query = searchQuery.toLowerCase();
    return customers.filter(
      (customer) =>
        (customer.firstname && customer.firstname.toLowerCase().includes(query)) ||
        (customer.surname && customer.surname.toLowerCase().includes(query)) ||
        (customer.email && customer.email.toLowerCase().includes(query)) ||
        (customer.id && customer.id.toLowerCase().includes(query)) ||
        (customer.status && customer.status.toLowerCase().includes(query)) ||
        (customer.address && customer.address.toLowerCase().includes(query)) ||
        (customer.addressCountryCode && customer.addressCountryCode.toLowerCase().includes(query)) ||
        (customer.passportNumber && customer.passportNumber.toLowerCase().includes(query)) ||
        (customer.passportFirstname && customer.passportFirstname.toLowerCase().includes(query)) ||
        (customer.passportSurname && customer.passportSurname.toLowerCase().includes(query))
    );
  }, [customers, searchQuery]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatUuid = (uuid) => {
    if (!uuid) return '—';
    // Extract the first segment (before the first hyphen)
    const firstSegment = uuid.split('-')[0];
    return firstSegment || uuid;
  };

  const handleAddCustomer = () => {
    // After adding a customer, refresh the list
    const fetchCustomers = async () => {
      try {
        const data = await api.getCustomers();
        setCustomers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to refresh customers:', err);
      }
    };
    fetchCustomers();
  };

  const getStatusClass = (status) => {
    const statusMap = {
      Verified: 'vt-status--success',
      Pending: 'vt-status--warning',
      Rejected: 'vt-status--danger',
      'Under Review': 'vt-status--info',
    };
    return statusMap[status] || '';
  };

  return (
    <div className="vt-kyc">
      <div className="vt-kycHeader">
        <div className="vt-kycSearch">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" className="vt-kycSearchIcon" aria-hidden="true">
            <path
              d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <input
            type="search"
            className="vt-kycSearchInput"
            placeholder="Search customers by name, email, ID, address, country code, passport number, or status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <button className="vt-btn vt-btn--primary" type="button" onClick={() => setIsModalOpen(true)}>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Add customer
        </button>
      </div>

      <div className="vt-kycTableWrapper vt-surface">
        {isLoading ? (
          <div className="vt-kycEmpty">Loading customers...</div>
        ) : error ? (
          <div className="vt-kycEmpty" style={{ color: 'var(--vt-danger)' }}>
            {error}
          </div>
        ) : (
          <table className="vt-kycTable">
            <thead>
              <tr>
                <th>ID</th>
                <th>First Name</th>
                <th>Surname</th>
                <th>Email</th>
                <th>Address</th>
                <th>Country</th>
                <th>Utility Bill</th>
                <th>Passport #</th>
                <th>Passport Name</th>
                <th>Passport DOB</th>
                <th>Passport</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan="13" className="vt-kycEmpty">
                    {searchQuery ? 'No customers found matching your search.' : 'No customers yet.'}
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => (
                  <tr 
                    key={customer.id}
                    onClick={() => navigate(`/customers/${customer.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="vt-kycId">{formatUuid(customer.id)}</td>
                    <td className="vt-kycName">{customer.firstname || '—'}</td>
                    <td className="vt-kycName">{customer.surname || '—'}</td>
                    <td className="vt-kycEmail">{customer.email || '—'}</td>
                    <td className="vt-kycAddress" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {customer.address || '—'}
                    </td>
                    <td className="vt-kycCountry">{customer.addressCountryCode || '—'}</td>
                    <td className="vt-kycUtilityBill" style={{ textAlign: 'center' }}>
                      {customer.utilityBillConfidence && customer.utilityBillConfidence > 80 ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#10b981' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="vt-kycPassportNumber" style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {customer.passportNumber || '—'}
                    </td>
                    <td className="vt-kycPassportName">
                      {customer.passportFirstname || customer.passportSurname 
                        ? `${customer.passportFirstname || ''} ${customer.passportSurname || ''}`.trim() 
                        : '—'}
                    </td>
                    <td className="vt-kycPassportDOB">
                      {customer.passportDateOfBirth ? formatDate(customer.passportDateOfBirth) : '—'}
                    </td>
                    <td className="vt-kycPassport" style={{ textAlign: 'center' }}>
                      {customer.passportConfidence && customer.passportConfidence > 80 ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#10b981' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <span className={['vt-status', getStatusClass(customer.status)].filter(Boolean).join(' ')}>
                        {customer.status || '—'}
                      </span>
                    </td>
                    <td className="vt-kycDate">{formatDate(customer.created)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {isModalOpen && <AddCustomerModal onClose={() => setIsModalOpen(false)} onSubmit={handleAddCustomer} />}
    </div>
  );
}
